import { Component, Input, Output, EventEmitter } from "@angular/core";
import { StatusService } from "../../../services/status.service";
import { UsersService } from "../../../services/users.service";
import { BranchService } from "../../../services/branch.service";
import { PrioritiesService } from "../../../services/priorities.service";
import { DepartmentsService } from "../../../services/departaments.service";

type FilterCode =
    | "by.id"
    | "by.title"
    | "by.assigned"
    | "by.branch"
    | "by.department"
    | "by.registered"
    | "by.status"
    | "by.priority"
    | "newest"
    | "active"
    | "archived"
    | "not";

interface PillDef {
    parentFilter: any;
    valueIndex: number | null;
    displayValue: string;
    icon: string;
    cssClass: string;
}

@Component({
    selector: "selected-filters",
    templateUrl: "./selected-filters.component.html",
    styleUrls: ["./selected-filters.component.css"],
})
export class SelectedFiltersComponent {
    @Input() selectedFilters: any[] = [];
    @Output() removeFilter = new EventEmitter<{ filter: any; valueIndex: number | null }>();

    constructor(
        private priority: PrioritiesService,
        private department: DepartmentsService,
        private branchService: BranchService,
        private userService: UsersService,
        private statusService: StatusService
    ) { }

    onRemovePill(pill: PillDef): void {
        this.removeFilter.emit({ filter: pill.parentFilter, valueIndex: pill.valueIndex });
    }

    get flatPills(): PillDef[] {
        const pills: PillDef[] = [];
        for (const filter of this.visibleFilters) {
            if (filter.code === 'match.any' && filter.filters?.length) {
                for (let i = 0; i < filter.filters.length; i++) {
                    pills.push({
                        parentFilter: filter,
                        valueIndex: i,
                        displayValue: this.leafDisplayValue(filter.filters[i]),
                        icon: this.getFilterIcon(filter),
                        cssClass: this.getFilterClass(filter),
                    });
                }
            } else if (filter.code === 'not' && filter.filters?.[0]?.code === 'match.any' && filter.filters[0].filters?.length) {
                const inner = filter.filters[0];
                for (let i = 0; i < inner.filters.length; i++) {
                    pills.push({
                        parentFilter: filter,
                        valueIndex: i,
                        displayValue: `Niet ${this.leafDisplayValue(inner.filters[i])}`,
                        icon: this.getFilterIcon(filter),
                        cssClass: this.getFilterClass(filter),
                    });
                }
            } else {
                pills.push({
                    parentFilter: filter,
                    valueIndex: null,
                    displayValue: this.getFilterDisplayValue(filter),
                    icon: this.getFilterIcon(filter),
                    cssClass: this.getFilterClass(filter),
                });
            }
        }
        return pills;
    }

    private leafDisplayValue(leaf: any): string {
        switch (leaf.code) {
            case 'by.registered':
            case 'by.assigned':
                return this.userService.labelForId(leaf.id);
            case 'by.branch':
                return this.branchService.labelForId(leaf.id);
            case 'by.department':
                return this.department.labelForId(leaf.id);
            case 'by.status':
                return this.statusService.labelForId(leaf.id);
            case 'by.priority':
                return this.priority.labelForId(leaf.id);
            case 'by.title':
                return leaf.searchphrase;
            case 'by.id':
                return String(leaf.id);
            default:
                return leaf.name || leaf.code;
        }
    }
    getFilterClass(filter: any): string {
        const iconClassMap: { [key in FilterCode]: string } = {
            "by.id": "selected-filter-tag",
            "by.title": "selected-filter-title",
            "by.assigned": "selected-filter-person",
            "by.branch": "selected-filter-business",
            "by.department": "selected-filter-apartment",
            "by.registered": "selected-filter-how_to_reg",
            "by.status": "selected-filter-check_circle",
            "by.priority": "selected-filter-error_circle",
            newest: "selected-filter-new_releases",
            active: "selected-filter-toggle_on",
            archived: "selected-filter-archive",
            not: "selected-filter-do_disturb_off",
        };

        if (filter.code === "not" && filter.filters?.length) {
            const inner = filter.filters[0];
            const innerClass = inner.code === "match.any"
                ? this.getFilterClass(inner)
                : (iconClassMap[inner.code as FilterCode] || "");
            return iconClassMap["not"] + " " + innerClass;
        }

        if (filter.code === "match.any" && filter.filters?.length) {
            const type = filter.filters[0].code;
            const sameType = filter.filters.every((f: any) => f.code === type);
            if (sameType && iconClassMap[type as FilterCode]) {
                return iconClassMap[type as FilterCode];
            }
        }

        return iconClassMap[filter.code as FilterCode] || "";
    }

    getFilterIcon(filter: any): string {
        if (filter.code === "not" && filter.filters?.length) {
            return this.getFilterIcon(filter.filters[0]);
        }

        if (filter.code === "match.any" && filter.filters?.length) {
            const type = filter.filters[0].code;

            const sameType = filter.filters.every((f: any) => f.code === type);
            if (!sameType) return "filter_alt";

            switch (type) {
                case "by.registered":
                    return "how_to_reg";
                case "by.assigned":
                    return "person";
                case "by.branch":
                    return "business";
                case "by.department":
                    return "apartment";
                case "by.status":
                    return "check_circle";
                case "by.priority":
                    return "flag";
                case "by.id":
                    return "tag";
                case "by.title":
                    return "title";
                default:
                    return "filter_alt";
            }
        }

        switch (filter.code) {
            case "by.id":
                return "tag";
            case "by.title":
                return "title";
            case "by.assigned":
                return "person";
            case "by.branch":
                return "business";
            case "by.department":
                return "apartment";
            case "by.registered":
                return "how_to_reg";
            case "by.status":
                return "check_circle";
            case "by.priority":
                return "flag";
            case "newest":
                return "new_releases";
            case "active":
                return "toggle_on";
            case "archived":
                return "archive";
            default:
                return "filter_alt";
        }
    }

    getFilterDisplayValue(filter: any): string {
        if (filter.code === "not" && filter.filters?.length) {
            const innerFilter = filter.filters[0];
            const innerLabel = this.getFilterDisplayValue(innerFilter);
            return `Niet ${innerLabel}`;
        }

        if (filter.code === "match.any" && filter.filters?.length) {
            const type = filter.filters[0]?.code;

            switch (type) {
                case "by.registered":
                    return filter.filters
                        .map((f: any) => this.userService.labelForId(f.id))
                        .join(", ");
                case "by.assigned":
                    return filter.filters
                        .map((f: any) => this.userService.labelForId(f.id))
                        .join(", ");
                case "by.branch":
                    return filter.filters
                        .map((f: any) => this.branchService.labelForId(f.id))
                        .join(", ");
                case "by.department":
                    return filter.filters
                        .map((f: any) => this.department.labelForId(f.id))
                        .join(", ");
                case "by.status":
                    return filter.filters
                        .map((f: any) => this.statusService.labelForId(f.id))
                        .join(", ");
                case "by.priority":
                    return filter.filters
                        .map((f: any) => this.priority.labelForId(f.id))
                        .join(", ");
                case "by.id":
                    return filter.filters.map((f: any) => f.id).join(", ");
                case "by.title":
                    return filter.filters.map((f: any) => f.searchphrase).join(", ");
            }
        }

        switch (filter.code) {
            case "by.id":
                return filter.id;
            case "by.title":
                return filter.searchphrase;
            case "by.assigned":
                return this.userService.labelForId(filter.id);
            case "by.branch":
                return this.branchService.labelForId(filter.id);
            case "by.department":
                return this.department.labelForId(filter.id);
            case "by.registered":
                return this.userService.labelForId(filter.id);
            case "by.status":
                return this.statusService.labelForId(filter.id);
            case "by.priority":
                return this.priority.labelForId(filter.id);
            case "newest":
                return filter.top;
            case "active":
                return filter.active;
            case "archived":
                return filter.archived;
            default:
                return filter.name || filter.code;
        }
    }
    get visibleFilters() {
        return this.selectedFilters?.filter(f => {
            if (f.code === 'active') return false;
            if (f.code === 'not') {
                const inner = f.filters?.[0];
                return !(inner?.code === 'by.status');
            }
            return true;
        }) || [];
    }
}
